<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { required } from "../../../../utils/validation";
import CheckInput from "../../../../components/ui/form/CheckInput.vue";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { ref, onMounted } from "vue";
import ModalFormTemplate from "../../../../components/ui/Templates/ModalFormTemplate.vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import NumberInput from "../../../../components/ui/form/NumberInput.vue";
import { copyText } from "../../../../utils/CommonFunctions";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  id: props.data?.id || "",
  outlets_id: props.data?.outlets_id || "",
  type: props.data?.type || "",
  only_selected_items: props.data?.only_selected_items || "",
  product_page_filters: props.data?.product_page_filters || "",
  products_per_page: props.data?.products_per_page || "",
  no_of_top_offer_products: props.data?.no_of_top_offer_products || "",
  no_of_best_selling_products: props.data?.no_of_best_selling_products || "",
  no_of_new_arrival_products: props.data?.no_of_new_arrival_products || "",
  show_points: props.data?.show_points || "",
  show_reviews: props.data?.show_reviews || "",
  show_out_of_stock_products: props.data?.show_out_of_stock_products || "",
  allow_pre_orders: props.data?.allow_pre_orders || "",
  uid: props.data?.uid || "",
  users_id: props.data?.users_id || "",
  active: props.data?.active || true,
});

setFieldRules("outlets_id", [required("Outlet is required")]);
setFieldRules("type", [required("Type is required")]);
setFieldRules("product_page_filters", [
  required("Product Page Filters is required"),
]);

const shopTypes = ref([{ id: "Website", name: "Website" }]);

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        resetForm();
      } else {
        response.data;
      }
    }
  }
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == "view";
  if (props.mode == "create") {
    resetForm();
  } else if (props.data) {
    setFormData(props.data);
  }
});



declare global {
  interface Window {
    copyText: (memo_no: string) => void;
  }
}
window.copyText = copyText;



</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    :tabList="[]"
    :formData="formData"
    @save="handleSubmit"
    @close="$emit('close')"
    width="45rem"
  >
    <template #form>
      <div class="grid md:grid-cols-4 gap-6">
        <div class="grid md:grid-cols-4 gap-6 col-span-3">
          <SearchSelect
            v-model="fields.outlets_id.value"
            label="Outlet"
            class="col-span-2"
            :options="shopTypes"
            :allow-new-record="true"
            :readonly="isReadOnly"
            :error="fields.outlets_id.touched ? fields.outlets_id.error : null"
            @blur="fields.outlets_id.touched = true"
          />

          <CheckInput
            v-model="fields.only_selected_items.value"
            :readonly="isReadOnly"
            label="Only Selected Items"
            :error="
              fields.only_selected_items.touched
                ? fields.only_selected_items.error
                : null
            "
            @blur="fields.only_selected_items.touched = true"
          />

          <NumberInput
            v-model="fields.products_per_page.value"
            label="Products Per Page"
            :readonly="isReadOnly"
            :min="0"
            :error="
              fields.products_per_page.touched
                ? fields.products_per_page.error
                : null
            "
            @blur="fields.products_per_page.touched = true"
          />

          <NumberInput
            v-model="fields.no_of_top_offer_products.value"
            label="No. of Top Offer Products"
            :readonly="isReadOnly"
            :min="0"
            :error="
              fields.no_of_top_offer_products.touched
                ? fields.no_of_top_offer_products.error
                : null
            "
            @blur="fields.no_of_top_offer_products.touched = true"
          />

          <NumberInput
            v-model="fields.no_of_best_selling_products.value"
            label="No. of Best Selling Products"
            :readonly="isReadOnly"
            :min="0"
            :error="
              fields.no_of_best_selling_products.touched
                ? fields.no_of_best_selling_products.error
                : null
            "
            @blur="fields.no_of_best_selling_products.touched = true"
          />

          <NumberInput
            v-model="fields.no_of_new_arrival_products.value"
            label="No. of New Arrival Products"
            :readonly="isReadOnly"
            :min="0"
            :error="
              fields.no_of_new_arrival_products.touched
                ? fields.no_of_new_arrival_products.error
                : null
            "
            @blur="fields.no_of_new_arrival_products.touched = true"
          />

          <CheckInput
            v-model="fields.show_points.value"
            :readonly="isReadOnly"
            label="Show Points"
            :error="
              fields.show_points.touched ? fields.show_points.error : null
            "
            @blur="fields.show_points.touched = true"
          />

          <CheckInput
            v-model="fields.show_reviews.value"
            :readonly="isReadOnly"
            label="Show Reviews"
            :error="
              fields.show_reviews.touched ? fields.show_reviews.error : null
            "
            @blur="fields.show_reviews.touched = true"
          />

          <CheckInput
            v-model="fields.show_out_of_stock_products.value"
            :readonly="isReadOnly"
            label="Show Out of Stock Products"
            :error="
              fields.show_out_of_stock_products.touched
                ? fields.show_out_of_stock_products.error
                : null
            "
            @blur="fields.show_out_of_stock_products.touched = true"
          />

          <CheckInput
            v-model="fields.allow_pre_orders.value"
            :readonly="isReadOnly"
            label="Allow Pre Orders"
            :error="
              fields.allow_pre_orders.touched
                ? fields.allow_pre_orders.error
                : null
            "
            @blur="fields.allow_pre_orders.touched = true"
          />

          <CheckInput
            v-model="fields.active.value"
            :readonly="isReadOnly"
            label="Active"
            :error="fields.active.touched ? fields.active.error : null"
            @blur="fields.active.touched = true"
          />
        </div>
      </div>
    </template>
  </ModalFormTemplate>
</template>
